/**
 * Mapbox Routing Service
 * 
 * Service for calculating road paths between stops using Mapbox Directions API.
 * Supports traffic-aware routing and bus routing.
 * Uses backend proxy to avoid CORS issues.
 */

import type { FixedRouteStop } from '../../types/fixedRoutes';
import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasFetch } from '../api/atlasBackendClient';

// Use backend proxy endpoint to avoid CORS issues
const API_BASE_URL = getAtlasApiBaseUrl();

// Timeout for Mapbox requests (in milliseconds)
const MAPBOX_TIMEOUT = 20000; // 20 seconds (slightly longer for traffic-aware routing)

/**
 * Check if Mapbox service is available via backend proxy
 */
async function checkMapboxAvailability(): Promise<boolean> {
  try {
    // Simple test request to check if backend Mapbox proxy is reachable
    const testUrl = `${API_BASE_URL}/api/mapbox/travel-time`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await atlasFetch(testUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        from_coords: [0, 0],
        to_coords: [0, 0],
        profile: 'driving-traffic',
      }),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    // 400 is expected for invalid coordinates, but means backend is reachable
    return response.status === 200 || response.status === 400 || response.status === 503;
  } catch (error) {
    return false;
  }
}

/**
 * Calculate route geometry between two stops using Mapbox via backend proxy
 * Returns the full coordinate sequence following roads with traffic-aware routing
 * 
 * @param fromStop - Starting stop
 * @param toStop - Destination stop
 * @param profile - Routing profile (default: 'driving-traffic' for traffic-aware)
 * @param departAt - Optional departure time for traffic-aware routing (ISO string or Date)
 */
export async function calculateRouteGeometry(
  fromStop: FixedRouteStop,
  toStop: FixedRouteStop,
  profile: string = 'driving-traffic',
  departAt?: string | Date
): Promise<[number, number][] | null> {
  try {
    if (!fromStop.coordinates || !toStop.coordinates) {
      throw new Error('Stops must have coordinates');
    }

    const url = `${API_BASE_URL}/api/mapbox/route`;
    
    // Convert departAt to ISO string if it's a Date
    let departAtStr: string | undefined;
    if (departAt instanceof Date) {
      departAtStr = departAt.toISOString();
    } else if (departAt) {
      departAtStr = departAt;
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), MAPBOX_TIMEOUT);

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_coords: [fromStop.coordinates.lat, fromStop.coordinates.lng],
          to_coords: [toStop.coordinates.lat, toStop.coordinates.lng],
          profile: profile,
          depart_at: departAtStr || 'now', // Use 'now' for current traffic conditions
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle network errors gracefully
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          console.warn('[Mapbox] Request timed out, using fallback');
          return null;
        }
      }
      
      console.error('[Mapbox] Network error:', fetchError);
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Mapbox] API error response:', errorText);
      throw new Error(`Mapbox API error: ${response.statusText}`);
    }

    const data = await response.json();

    // Backend returns geometry as [lat, lng] coordinates
    const coordinatesSequence: [number, number][] = data.geometry || [];

    return coordinatesSequence.length > 0 ? coordinatesSequence : null;
  } catch (error) {
    console.error('Mapbox route calculation error:', error);
    return null;
  }
}

/**
 * Calculate travel time and distance between two stops using Mapbox
 * Uses traffic-aware routing by default for accurate bus routing
 * Returns time in minutes and distance in meters
 * 
 * @param fromStop - Starting stop
 * @param toStop - Destination stop
 * @param profile - Routing profile (default: 'driving-traffic' for traffic-aware)
 * @param departAt - Optional departure time for traffic-aware routing
 */
export async function calculateTravelTimeAndDistance(
  fromStop: FixedRouteStop,
  toStop: FixedRouteStop,
  profile: string = 'driving-traffic',
  departAt?: string | Date
): Promise<{ travelTimeMinutes: number; distanceMeters: number }> {
  console.log('[Mapbox] calculateTravelTimeAndDistance called', { 
    fromStop: fromStop.name, 
    toStop: toStop.name,
    profile,
    departAt 
  });
  
  try {
    if (!fromStop.coordinates || !toStop.coordinates) {
      console.error('[Mapbox] Missing coordinates', { fromStop, toStop });
      throw new Error('Stops must have coordinates');
    }

    const url = `${API_BASE_URL}/api/mapbox/travel-time`;

    // Convert departAt to ISO string if it's a Date
    let departAtStr: string | undefined;
    if (departAt instanceof Date) {
      departAtStr = departAt.toISOString();
    } else if (departAt) {
      departAtStr = departAt;
    }

    console.log('[Mapbox] Making request to backend proxy:', url);

    // Add timeout to fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), MAPBOX_TIMEOUT);

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_coords: [fromStop.coordinates.lat, fromStop.coordinates.lng],
          to_coords: [toStop.coordinates.lat, toStop.coordinates.lng],
          profile: profile,
          depart_at: departAtStr || 'now', // Use 'now' for current traffic conditions
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle network errors (connection refused, timeout, etc.)
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          throw new Error('Mapbox request timed out. The routing service may be unavailable or slow. Please try again later.');
        }
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
          throw new Error('Unable to connect to routing service. Please check your internet connection and ensure the backend server is running.');
        }
      }
      
      throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
    }

    console.log('[Mapbox] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Mapbox] API error response:', errorText);
      
      // Handle service not configured error
      if (response.status === 503) {
        throw new Error('Mapbox service is not configured. Please set MAPBOX_ACCESS_TOKEN in the backend environment variables.');
      }
      
      throw new Error(`Mapbox API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[Mapbox] Response data:', { 
      duration_seconds: data.duration_seconds, 
      distance_meters: data.distance_meters 
    });

    // Backend returns duration in seconds
    // Convert to minutes and round
    const durationSeconds = data.duration_seconds;
    if (!durationSeconds || isNaN(durationSeconds)) {
      console.error('[Mapbox] Invalid duration', durationSeconds);
      throw new Error('Invalid duration returned from Mapbox');
    }
    const durationMinutes = Math.round(durationSeconds / 60);
    
    // Get distance in meters
    const distanceMeters = data.distance_meters || 0;
    if (distanceMeters && isNaN(distanceMeters)) {
      console.warn('[Mapbox] Invalid distance', distanceMeters);
    }
    
    console.log('[Mapbox] Calculated travel time:', durationMinutes, 'minutes (', durationSeconds, 'seconds), distance:', distanceMeters, 'meters');

    return {
      travelTimeMinutes: durationMinutes,
      distanceMeters: distanceMeters || 0,
    };
  } catch (error) {
    console.error('[Mapbox] Travel time and distance calculation error:', error);
    throw error;
  }
}

/**
 * Calculate travel times and distances for multiple segments in a single batch request
 * Uses Mapbox Matrix API for efficient batch processing with traffic-aware routing
 * 
 * @param segments - Array of stop pairs to calculate
 * @param profile - Routing profile (default: 'driving-traffic' for traffic-aware)
 * @param departAt - Optional departure time for traffic-aware routing
 */
export async function calculateTravelTimesAndDistancesBatch(
  segments: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop }>,
  profile: string = 'driving-traffic',
  departAt?: string | Date
): Promise<Array<{ travelTimeMinutes: number; distanceMeters: number }>> {
  console.log('[Mapbox] calculateTravelTimesAndDistancesBatch called', { 
    segmentCount: segments.length,
    profile,
    departAt 
  });
  
  try {
    if (segments.length === 0) {
      return [];
    }

    // Validate all stops have coordinates and they are valid
    for (const segment of segments) {
      if (!segment.fromStop.coordinates || !segment.toStop.coordinates) {
        throw new Error('All stops must have coordinates');
      }
      
      const fromLat = segment.fromStop.coordinates.lat;
      const fromLng = segment.fromStop.coordinates.lng;
      const toLat = segment.toStop.coordinates.lat;
      const toLng = segment.toStop.coordinates.lng;
      
      // Validate coordinate values
      if (typeof fromLat !== 'number' || typeof fromLng !== 'number' ||
          typeof toLat !== 'number' || typeof toLng !== 'number' ||
          isNaN(fromLat) || isNaN(fromLng) || isNaN(toLat) || isNaN(toLng) ||
          !isFinite(fromLat) || !isFinite(fromLng) || !isFinite(toLat) || !isFinite(toLng)) {
        throw new Error(`Invalid coordinates in stop: ${segment.fromStop.name || segment.fromStop.id} or ${segment.toStop.name || segment.toStop.id}`);
      }
      
      // Validate coordinate ranges
      if (fromLat < -90 || fromLat > 90 || fromLng < -180 || fromLng > 180 ||
          toLat < -90 || toLat > 90 || toLng < -180 || toLng > 180) {
        throw new Error(`Coordinates out of valid range for stop: ${segment.fromStop.name || segment.fromStop.id} or ${segment.toStop.name || segment.toStop.id}`);
      }
    }

    // Collect all unique coordinates
    const coordinateMap = new Map<string, number>(); // coord string -> index
    const coordinates: [number, number][] = [];
    const pairs: [number, number][] = [];

    segments.forEach((segment) => {
      const fromLat = segment.fromStop.coordinates!.lat;
      const fromLng = segment.fromStop.coordinates!.lng;
      const toLat = segment.toStop.coordinates!.lat;
      const toLng = segment.toStop.coordinates!.lng;
      
      const fromCoord = `${fromLat},${fromLng}`;
      const toCoord = `${toLat},${toLng}`;

      // Add from coordinate if not seen
      if (!coordinateMap.has(fromCoord)) {
        coordinateMap.set(fromCoord, coordinates.length);
        coordinates.push([fromLat, fromLng]);
      }

      // Add to coordinate if not seen
      if (!coordinateMap.has(toCoord)) {
        coordinateMap.set(toCoord, coordinates.length);
        coordinates.push([toLat, toLng]);
      }

      // Record pair indices
      const fromIdx = coordinateMap.get(fromCoord)!;
      const toIdx = coordinateMap.get(toCoord)!;
      pairs.push([fromIdx, toIdx]);
    });
    
    // Validate we have coordinates and pairs
    if (coordinates.length === 0) {
      throw new Error('No valid coordinates found in segments');
    }
    
    if (pairs.length === 0) {
      throw new Error('No valid pairs found in segments');
    }

    console.log('[Mapbox] Batch request:', { 
      uniqueCoordinates: coordinates.length, 
      pairs: pairs.length,
      sampleCoordinates: coordinates.slice(0, 3),
      samplePairs: pairs.slice(0, 3),
      allCoordinates: coordinates, // Log all coordinates for debugging
      allPairs: pairs // Log all pairs for debugging
    });
    
    // Additional validation: ensure coordinates look like real lat/lng values
    const invalidCoords = coordinates.filter(coord => 
      Math.abs(coord[0]) > 90 || Math.abs(coord[1]) > 180 ||
      (Math.abs(coord[0]) < 0.1 && Math.abs(coord[1]) < 0.1) // Suspiciously small values
    );
    if (invalidCoords.length > 0) {
      console.error('[Mapbox] Suspicious coordinates detected:', invalidCoords);
      throw new Error(`Invalid coordinates detected: ${invalidCoords.length} coordinates appear to be invalid (lat: ${invalidCoords[0]?.[0]}, lng: ${invalidCoords[0]?.[1]})`);
    }

    // Convert departAt to ISO string if it's a Date
    let departAtStr: string | undefined;
    if (departAt instanceof Date) {
      departAtStr = departAt.toISOString();
    } else if (departAt) {
      departAtStr = departAt;
    }

    const requestBody = {
      coordinates: coordinates,
      pairs: pairs,
      profile: profile,
      depart_at: departAtStr || 'now', // Use 'now' for current traffic conditions
    };
    
    // Validate request body before sending
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      throw new Error('Invalid coordinates array: must be a non-empty array');
    }
    
    if (!Array.isArray(pairs) || pairs.length === 0) {
      throw new Error('Invalid pairs array: must be a non-empty array');
    }
    
    // Validate all coordinates are valid tuples
    for (let i = 0; i < coordinates.length; i++) {
      const coord = coordinates[i];
      if (!Array.isArray(coord) || coord.length !== 2 ||
          typeof coord[0] !== 'number' || typeof coord[1] !== 'number' ||
          isNaN(coord[0]) || isNaN(coord[1])) {
        throw new Error(`Invalid coordinate at index ${i}: ${JSON.stringify(coord)}`);
      }
    }
    
    // Validate all pairs reference valid coordinate indices
    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i];
      if (!Array.isArray(pair) || pair.length !== 2 ||
          typeof pair[0] !== 'number' || typeof pair[1] !== 'number' ||
          pair[0] < 0 || pair[0] >= coordinates.length ||
          pair[1] < 0 || pair[1] >= coordinates.length) {
        throw new Error(`Invalid pair at index ${i}: ${JSON.stringify(pair)} (coordinates length: ${coordinates.length})`);
      }
    }

    const url = `${API_BASE_URL}/api/mapbox/travel-time-batch`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), MAPBOX_TIMEOUT * 2); // Allow more time for batch requests

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          throw new Error('Mapbox batch request timed out. The routing service may be unavailable or slow.');
        }
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
          throw new Error('Unable to connect to routing service. Please check your internet connection and ensure the backend server is running.');
        }
      }
      
      throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Mapbox] Batch API error response:', errorText);
      console.error('[Mapbox] Request body that caused error:', JSON.stringify(requestBody, null, 2));
      
      // Handle service not configured error
      if (response.status === 503) {
        throw new Error('Mapbox service is not configured. Please set MAPBOX_ACCESS_TOKEN in the backend environment variables.');
      }
      
      // Provide more detailed error message for 400 Bad Request
      if (response.status === 400) {
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(`Mapbox API error: 400 Bad Request - ${errorData.detail || errorData.message || errorText}`);
        } catch {
          throw new Error(`Mapbox API error: 400 Bad Request - ${errorText}`);
        }
      }
      
      throw new Error(`Mapbox API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[Mapbox] Batch response received:', { resultCount: data.results?.length });

    // Convert results to minutes and return
    return data.results.map((result: { duration_seconds: number; distance_meters: number }) => ({
      travelTimeMinutes: Math.round(result.duration_seconds / 60),
      distanceMeters: result.distance_meters || 0,
    }));
  } catch (error) {
    console.error('[Mapbox] Batch travel time and distance calculation error:', error);
    throw error;
  }
}

/**
 * Calculate route geometry for a complete route with multiple stops
 * Returns an array of coordinate sequences for each segment
 * Uses traffic-aware routing for bus routing
 * 
 * @param stops - Array of stops in route order
 * @param profile - Routing profile (default: 'driving-traffic' for traffic-aware)
 * @param departAt - Optional departure time for traffic-aware routing
 */
export async function calculateRouteGeometryForStops(
  stops: FixedRouteStop[],
  profile: string = 'driving-traffic',
  departAt?: string | Date
): Promise<[number, number][][]> {
  const segments: [number, number][][] = [];

  for (let i = 0; i < stops.length - 1; i++) {
    const fromStop = stops[i];
    const toStop = stops[i + 1];

    try {
      const geometry = await calculateRouteGeometry(fromStop, toStop, profile, departAt);
      if (geometry && geometry.length > 0) {
        segments.push(geometry);
      } else {
        // Don't create fallback - throw error instead to prevent straight-line routes
        throw new Error(`Failed to calculate route geometry between stops ${i} and ${i + 1}`);
      }

      // Minimal rate limiting: wait 100ms between requests
      // This prevents overwhelming the server while maintaining responsiveness
      if (i < stops.length - 2) { // Don't delay after last segment
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error(`Failed to calculate route segment ${i} to ${i + 1}:`, error);
      // Don't create fallback - rethrow to prevent straight-line routes
      throw error;
    }
  }

  return segments;
}

/**
 * Calculate bus route with traffic-aware routing
 * Convenience function for bus routing that uses driving-traffic profile
 * 
 * @param fromStop - Starting stop
 * @param toStop - Destination stop
 * @param departAt - Optional departure time for traffic-aware routing
 */
export async function calculateBusRoute(
  fromStop: FixedRouteStop,
  toStop: FixedRouteStop,
  departAt?: string | Date
): Promise<{ 
  geometry: [number, number][] | null; 
  travelTimeMinutes: number; 
  distanceMeters: number 
}> {
  const [geometry, travelTime] = await Promise.all([
    calculateRouteGeometry(fromStop, toStop, 'driving-traffic', departAt),
    calculateTravelTimeAndDistance(fromStop, toStop, 'driving-traffic', departAt)
  ]);

  return {
    geometry,
    travelTimeMinutes: travelTime.travelTimeMinutes,
    distanceMeters: travelTime.distanceMeters,
  };
}

